import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Bell, Mail, Loader2 } from 'lucide-react';

export default function ClientSettings() {
  const { api, user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(user?.notification_preferences || {
    email_new_ticket: true,
    email_status_change: true,
    email_new_message: true,
    email_ticket_resolved: true,
    in_app_notifications: true
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/auth/notification-preferences', preferences);
      toast.success('Préférences enregistrées');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="client-settings" className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Paramètres</h1>
        <p className="text-slate-600 dark:text-zinc-400 mt-1">
          Gérez vos préférences de notification
        </p>
      </div>

      <Card className="border-slate-200 dark:border-zinc-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Notifications par email
          </CardTitle>
          <CardDescription>
            Choisissez quand recevoir des emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email_new_ticket" className="font-medium">
                Création de ticket
              </Label>
              <p className="text-sm text-slate-500 dark:text-zinc-400">
                Recevoir une confirmation à la création d'un ticket
              </p>
            </div>
            <Switch
              id="email_new_ticket"
              checked={preferences.email_new_ticket}
              onCheckedChange={(checked) => setPreferences({ ...preferences, email_new_ticket: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email_status_change" className="font-medium">
                Changement de statut
              </Label>
              <p className="text-sm text-slate-500 dark:text-zinc-400">
                Être informé des mises à jour de statut
              </p>
            </div>
            <Switch
              id="email_status_change"
              checked={preferences.email_status_change}
              onCheckedChange={(checked) => setPreferences({ ...preferences, email_status_change: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email_new_message" className="font-medium">
                Nouveaux messages
              </Label>
              <p className="text-sm text-slate-500 dark:text-zinc-400">
                Recevoir un email pour chaque réponse
              </p>
            </div>
            <Switch
              id="email_new_message"
              checked={preferences.email_new_message}
              onCheckedChange={(checked) => setPreferences({ ...preferences, email_new_message: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email_ticket_resolved" className="font-medium">
                Ticket résolu
              </Label>
              <p className="text-sm text-slate-500 dark:text-zinc-400">
                Être notifié quand un ticket est résolu
              </p>
            </div>
            <Switch
              id="email_ticket_resolved"
              checked={preferences.email_ticket_resolved}
              onCheckedChange={(checked) => setPreferences({ ...preferences, email_ticket_resolved: checked })}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 dark:border-zinc-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notifications in-app
          </CardTitle>
          <CardDescription>
            Notifications dans l'application
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="in_app_notifications" className="font-medium">
                Activer les notifications
              </Label>
              <p className="text-sm text-slate-500 dark:text-zinc-400">
                Recevoir des notifications en temps réel dans l'application
              </p>
            </div>
            <Switch
              id="in_app_notifications"
              checked={preferences.in_app_notifications}
              onCheckedChange={(checked) => setPreferences({ ...preferences, in_app_notifications: checked })}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          className="bg-emerald-600 hover:bg-emerald-700"
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Enregistrement...
            </>
          ) : (
            'Enregistrer les préférences'
          )}
        </Button>
      </div>
    </div>
  );
}
